import React, { useState, useEffect, useCallback } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useFetch } from "../../hooks/useFetch";
import SpinnerLoading from "../../component/Loading/SpinnerLoading";
import { useTranslation } from "react-i18next";
import SalesPackageSection from "./SalesPackageSection";
import SlideContent from "./SlideContent";
import API_ENDPOINTS from "../../api";

// Import your images exactly as before
import location from "../../images/Landing/landing1.PNG";
import land1 from "../../images/Landing/landing1.PNG";
import info from "../../images/Landing/landing1.PNG";

const LandingPage = () => {
  const { data: Sale, loading, error } = useFetch(API_ENDPOINTS.OFFERS.GET_ALL);
  const [index, setIndex] = useState(0);
  const [packageIndex, setPackageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isArabic = i18n.language === 'ar';


  // Your original slides data kept exactly as is
  const slides = [
    {
      id: 1,
      header: t("home.landingPage.slides.0.header"),
      content: t("home.landingPage.slides.0.content"),
      image: location,
      labelBtn: t("home.landingPage.slides.0.labelBtn"),
      path: "/location",
    },
    {
      id: 2,
      header: t("home.landingPage.slides.1.header"),
      content: t("home.landingPage.slides.1.content"),
      image: land1,
      labelBtn: t("home.landingPage.slides.1.labelBtn"),
      path: "/information",
    },
    {
      id: 3,
      header: t("home.landingPage.slides.2.header"),
      content: t("home.landingPage.slides.2.content"),
      image: info,
      labelBtn: t("home.landingPage.slides.2.labelBtn"),
      path: "/package",
    },
  ];

  const validSales = Sale?.sales?.filter((sale) => new Date(sale.endDate) > new Date()) || [];
  const currentPackage = validSales.length > 0 && validSales[packageIndex];

  const changeSlide = useCallback((direction) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setIndex((prevIndex) => (prevIndex + direction + slides.length) % slides.length);
      setIsTransitioning(false);
    }, 400);
  }, [slides.length]);

  useEffect(() => {
    const slideTimer = setInterval(() => changeSlide(1), 7000);
    const packageTimer = setInterval(() => {
      if (validSales.length > 0) {
        setPackageIndex((prevIndex) => (prevIndex + 1) % validSales.length);
      }
    }, 5000);
    return () => { clearInterval(slideTimer); clearInterval(packageTimer); };
  }, [validSales.length, changeSlide]);

  if (loading) return <SpinnerLoading />;
  if (error) return <div className="text-center text-red-500 mt-20">{error.message}</div>;

  return (
    <div className="relative min-h-screen bg-white">
      {/* Dynamic Background Wrapper */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1000 transform ${isTransitioning ? 'opacity-0 scale-105' : 'opacity-100 scale-100'}`}
          style={{ backgroundImage: `url(${slides[index].image})` }}
        />
        {/* Modern Overlay - Glass for Desktop, Strong for Mobile */}
        <div className={`absolute inset-0 ${isArabic 
      ? "bg-gradient-to-l from-white via-white/80 to-transparent" 
      : "bg-gradient-to-r from-white via-white/80 to-transparent"} hidden lg:block`} />
        <div className="absolute inset-0 bg-white/80 lg:hidden block" dir={isArabic ? "rtl" : "ltr"} />
      </div>

      {/* Content Container */}
      <div className="relative z-10  container mx-auto px-6 !mt-[100px] lg:px-12 flex flex-col lg:flex-row items-center justify-between min-h-screen pt-[100px] lg:pt-0 gap-10">
        
        {/* Text Section */}
        <div className={`flex-1 max-w-2xl  transition-all duration-500 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
           <SlideContent slide={slides[index]} onClick={() => navigate(slides[index].path)} i18n={i18n} />
        </div>

        {/* Sale Package Section */}
        {currentPackage && (
          <div className="w-full mt-[80px] lg:w-auto flex justify-center pb-6 lg:pb-0">
            <SalesPackageSection currentPackage={currentPackage} t={t} i18n={i18n} />
          </div>
        )}
      </div>

      {/* Navigation Controls */}
      <div className="absolute z-20 bottom-5 left-6 right-6 lg:left-12 flex items-center justify-between">
        {/* Arrows */}
        <div className="flex gap-4">
          <button onClick={() => changeSlide(-1)} className="p-2 bg-white shadow-xl rounded-full hover:bg-gray-50 transition-all active:scale-90">
            <FaChevronLeft className="text-primeColor-600" />
          </button>
          <button onClick={() => changeSlide(1)} className="p-2 bg-white shadow-xl rounded-full hover:bg-gray-50 transition-all active:scale-90">
            <FaChevronRight className="text-primeColor-500" />
          </button>
        </div>
        
        {/* Dots */}
        <div className="flex gap-3">
          {slides.map((_, i) => (
            <div 
              key={i} 
              className={`h-1.5 rounded-full transition-all duration-500 ${i === index ? 'w-10 bg-primeColor-600' : 'w-2 bg-gray-300'}`} 
            />
          ))}
        </div>

        
      </div>
    </div>
  );
};

export default LandingPage;